import { HttpRequestState } from "src/app/shared/store/shared/http-request-state";
import { Product } from "../../shared/product";

export interface QuoteFeatureState extends HttpRequestState {
  // The applicationId to which this particular quote is connected to.
  // If this value is set, we know that this quote should be considered 'locked'.
  applicationId: string | undefined;
  quoteId: string | undefined;
  product: Product | undefined;
  mobilePhoneNumber: string | undefined;
  email: string | undefined;
  monthOfPregnancy: number | undefined | null;
  gender: Gender | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  nationalityCode: string | undefined;
  swissResidencePermit: SwissResidencePermit | undefined | null;
  dateOfBirth: Date | undefined;
  technicalAgeInYears: number | undefined;
  addressLines: string | undefined;
  city: string | undefined;
  postcode: number | undefined;
  country: string | undefined;
  civilStatus: CivilStatus | undefined;
  isSmoker: boolean | undefined;
  occupationId: number | undefined;
  hasTermsAccepted: boolean | undefined;
  insuranceStartDate: Date | undefined | null;
  insuranceTermInYears: number | undefined | null;
  insuredSum: number | undefined;
  heightInCm: number | undefined;
  weightInKg: number | undefined;
  hasUsCitizenship: boolean | undefined;
  hasSwissTaxResidence: boolean | undefined;
  isLegacyApplication: boolean | undefined | null;
  useStandardBeneficiaries: boolean | undefined;
  beneficiaries: Beneficiary[] | undefined;
  hasUnderwritingQuestionsPassed: boolean | undefined | null;
  answeredUnderwritingQuestions:
    | UnderwritingQuestionAnswer[]
    | undefined
    | null;
  underwritingQuestions: UnderwritingQuestion[] | undefined;
  grossPremium: number | undefined;
  netPremium: number | undefined;
  year: number | undefined;
  conversionCapitalDevelopments: ConversionCapitalDevelopment[] | undefined;
  productConfiguration: ProductConfiguration | undefined;
  attributionCode: string | undefined;
  dataAnalyticsCampaign: DataAnalyticsCampaign | undefined;
}

export function createEmptyQuoteFeatureState() {
  const quoteFeatureState: QuoteFeatureState = {
    applicationId: undefined,
    quoteId: undefined,
    product: undefined,
    mobilePhoneNumber: undefined,
    email: undefined,
    monthOfPregnancy: undefined,
    gender: undefined,
    firstName: undefined,
    lastName: undefined,
    nationalityCode: undefined,
    swissResidencePermit: undefined,
    dateOfBirth: undefined,
    technicalAgeInYears: undefined,
    addressLines: undefined,
    city: undefined,
    postcode: undefined,
    country: undefined,
    civilStatus: undefined,
    isSmoker: undefined,
    occupationId: undefined,
    hasTermsAccepted: undefined,
    insuranceStartDate: undefined,
    insuranceTermInYears: undefined,
    insuredSum: undefined,
    heightInCm: undefined,
    weightInKg: undefined,
    hasUsCitizenship: undefined,
    hasSwissTaxResidence: undefined,
    isLegacyApplication: undefined,
    useStandardBeneficiaries: undefined,
    beneficiaries: undefined,
    hasUnderwritingQuestionsPassed: undefined,
    answeredUnderwritingQuestions: undefined,
    underwritingQuestions: undefined,
    grossPremium: undefined,
    netPremium: undefined,
    year: undefined,
    conversionCapitalDevelopments: undefined,
    productConfiguration: undefined,
    attributionCode: undefined,
    dataAnalyticsCampaign: undefined,
  };

  return quoteFeatureState;
}

export enum Gender {
  MALE = "M",
  FEMALE = "F",
}

export enum SwissResidencePermit {
  B = "B",
  C = "C",
}

export enum BeneficiaryType {
  STANDARD = "standard",
  INDIVIDUAL = "individual",
}

export enum BeneficiarySubType {
  PERSON = "person",
  ORGANIZATION = "organization",
}

export enum CivilStatus {
  SINGLE = "single",
  MARRIED = "married",
  DIVORCED = "divorced",
  WIDOWED = "widowed",
  REGISTERED_PARTNERSHIP = "registeredPartnership",
}

export interface ConversionCapitalDevelopment {
  date: Date;
  amount: number;
}

////////////////////////////////////////////////////////////////////////////////////////////////
// Beneficiary
////////////////////////////////////////////////////////////////////////////////////////////////

export type Beneficiary = PersonBeneficiary | OrganizationBeneficiary;

export interface BaseBeneficiary {
  index: number;
  percentage: number;
  type: BeneficiarySubType;
}

export interface PersonBeneficiary extends BaseBeneficiary {
  type: BeneficiarySubType.PERSON;
  dateOfBirth: Date;
  firstName: string;
  lastName: string;
}

export interface OrganizationBeneficiary extends BaseBeneficiary {
  type: BeneficiarySubType.ORGANIZATION;
  name: string;
  city: string;
  postcode: number;
}

////////////////////////////////////////////////////////////////////////////////////////////////
// Underwriting Questions
////////////////////////////////////////////////////////////////////////////////////////////////

export interface UnderwritingQuestion {
  id: string;
  label: string;
  additionalInfo: string | undefined;
}

// The answers to the underwriting questions are only kept on client and never sent to the server.
export interface UnderwritingQuestionAnswer {
  questionId: string;
  saidYes: boolean | undefined;
}

////////////////////////////////////////////////////////////////////////////////////////////////
// Product Configuration
////////////////////////////////////////////////////////////////////////////////////////////////

export interface ProductConfiguration {
  minimumInsuredSum: number;
  maximumInsuredSum: number;
  maximumInsuredSumWithoutMedicalCheckup: number;
  minimumEntryAge: number;
  maximumEntryAge: number;
  maximumCoverageAge: number | undefined;
  statutoryRetirementAgeFemale: number;
  statutoryRetirementAgeMale: number;
  cancellationInterval: CancellationInterval;
  termDocuments: TermDocument[];
  bmiConfiguration: BmiConfiguration;
  allowedInsuranceStartDates: Date[];
}

export interface BmiConfiguration {
  minHeightInCm: number;
  maxHeightInCm: number;
  minWeightInKg: number;
  maxWeightInKg: number;
  minBmi: number;
  maxBmi: number;
}

export interface TermDocument {
  typeOfDocument: StaticDocumentType;
  internalDocumentName: string;
}

export enum CancellationInterval {
  YEARLY = "yearly",
}

/**
 * An enum which contains all static documents (not only the ones from term life insurance)
 */
export enum StaticDocumentType {
  // INFORMATIONEN ZUR DATENBEARBEITUNG BEI PAX
  INFO_DATA_PROCESSING = "infoDataProcessing",
  // VEREINBARUNG ZUR KOMMUNIKATION VIA E-MAIL
  AGREEMENT_EMAIL_COMMUNICATION = "agreementEmailCommunication",
  // ALLGEMEINE BEDINGUNGEN FÜR DIE EINZEL-LEBENSVERSICHERUNG
  TERMS_GENERAL_INDIVIDUAL_LIFE_INSURANCE = "termsGeneralIndividualLifeInsurance",
  // ERGÄNZENDE BEDINGUNGEN FÜR TODESFALLVERSICHERUNGEN
  TERMS_SUPPLEMENT_TERM_LIFE_INSURANCE = "termsSupplementTermLifeInsurance",
  // ZUSATZBEDINGUNGEN FÜR DIE VERSICHERUNG VON NICHTRAUCHERN
  TERMS_NON_SMOKER = "termsNonSmoker",
  // INFORMATION FÜR DEN ELEKTRONISCHEN DIREKTVERTRIEB
  INFO_ELECTRONIC_DIRECT_SALES = "infoElectronicDirectSales",
  // PRODUKTINFORMATIONEN TODESFALLVERSICHERUNG
  INFO_TERM_LIFE_INSURANCE_PRODUCT = "infoTermLifeInsuranceProduct",
}

////////////////////////////////////////////////////////////////////////////////////////////////
// Data Analytics
////////////////////////////////////////////////////////////////////////////////////////////////

export interface DataAnalyticsCampaign {
  /**
   * Those values are shared between the url and the cookie.
   */
  utmSource: string | undefined;
  utmMedium: string | undefined;
  utmCampaign: string | undefined;
  utmContent: string | undefined;
  utmTerm: string | undefined;
  utmId: string | undefined;
  gclId: string | undefined;

  /**
   * This parameter should only exist in the cookie.
   */
  referrer: string | undefined;
}
